import React, { useEffect, useState } from 'react';
import {
    Row, Col, Card, Badge, Button, Spinner,
    Container, Modal, Form, Alert, InputGroup
} from 'react-bootstrap';
import {
    LuArrowLeft, LuPrinter, LuUser,
    LuCreditCard, LuCircleAlert, LuTag, LuBriefcase,
    LuExternalLink, LuCopy, LuCheck, LuRedo, LuReceipt
} from 'react-icons/lu';
import { useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
    fetchTransactionDetail,
    refundTransaction,
    fetchExistingPaymentLink,
    resetPaymentLinks
} from '../../../redux/reducers/transactionSlice';
import DirectPaymentLink from '../../Payment/DirectPaymentLink';
import moment from 'moment';
import { toast } from 'react-toastify';
import { formatPhoneNumber } from '../../../utils/phoneUtils';

const TransactionDetail = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { id } = useParams();

    const { selectedTransaction, loading, paymentLinkUrl, paymentLinkStatus } = useSelector(
        (state) => state.transaction
    );

    const [showRefundModal, setShowRefundModal] = useState(false);
    const [refundType, setRefundType] = useState('full');
    const [refundAmount, setRefundAmount] = useState('');
    const [refundReason, setRefundReason] = useState('');
    const [isProcessing, setIsProcessing] = useState(false);
    const [copied, setCopied] = useState(false);

    useEffect(() => {
        if (id) {
            dispatch(fetchTransactionDetail(id)).unwrap().then((res) => {
                if (res.status === 'pending') {
                    dispatch(fetchExistingPaymentLink(id));
                }
            });
        }
    }, [dispatch, id]);

    const handleCopyLink = () => {
        if (paymentLinkUrl) {
            navigator.clipboard.writeText(paymentLinkUrl);
            setCopied(true);
            toast.success('Link copied to clipboard!');
            setTimeout(() => setCopied(false), 2000);
        }
    };

    const handleRefund = async () => {
        const amountToRefund = refundType === 'full' ? undefined : parseFloat(refundAmount);
        setIsProcessing(true);
        try {
            await dispatch(refundTransaction({
                transactionId: id,
                amount: amountToRefund,
                reason: refundReason
            })).unwrap();
            setShowRefundModal(false);
            dispatch(fetchTransactionDetail(id));
        } catch (err) {
            // error toast handled in thunk
        } finally {
            setIsProcessing(false);
        }
    };

    // ── Loading / Empty states ──────────────────────────────────────────────
    if (loading && !selectedTransaction) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '60vh' }}>
                <Spinner animation="border" style={{ color: '#28B0C4' }} />
            </div>
        );
    }

    if (!selectedTransaction || !selectedTransaction.financials) {
        return (
            <Container className="py-5 text-center">
                <h4 className="text-muted">Transaction not found.</h4>
                <Button variant="link" onClick={() => navigate('/transaction')}>
                    ← Back to Transactions
                </Button>
            </Container>
        );
    }

    const { financials, status, createdAt, paymentMethod, customerId, description, invoiceId } = selectedTransaction;

    const statusColorMap = {
        captured:   { bg: 'success', text: 'text-success', soft: 'bg-success-subtle' },
        pending:    { bg: 'warning', text: 'text-warning', soft: 'bg-warning-subtle' },
        refunded:   { bg: 'danger',  text: 'text-danger',  soft: 'bg-danger-subtle'  },
        voided:     { bg: 'secondary', text: 'text-secondary', soft: 'bg-secondary-subtle' },
        authorized: { bg: 'info', text: 'text-info', soft: 'bg-info-subtle' },
    };
    const colors = statusColorMap[status] || statusColorMap.authorized;

    return (
        <Container className="py-4">
            {/* ── Top Nav ─────────────────────────────────────────────────────── */}
            <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-2">
                <Button
                    variant="link"
                    className="text-decoration-none text-muted p-0 d-flex align-items-center gap-2 fw-bold"
                    onClick={() => navigate('/transaction')}
                >
                    <LuArrowLeft size={18} /> Back to Transactions
                </Button>
                <div className="d-flex gap-2">
                    {status === 'captured' && (
                        <Button
                            variant="outline-danger"
                            className="rounded-pill px-3 shadow-sm d-flex align-items-center gap-2 fw-bold"
                            onClick={() => setShowRefundModal(true)}
                        >
                            <LuRedo size={16} /> Refund
                        </Button>
                    )}
                    <Button
                        variant="outline-dark"
                        className="rounded-pill px-3 shadow-sm d-flex align-items-center gap-2 fw-bold"
                        onClick={() => window.print()}
                    >
                        <LuPrinter size={16} /> Print Receipt
                    </Button>
                </div>
            </div>

            {/* ── Pending Payment Link Alert ───────────────────────────────────── */}
            {status === 'pending' && (
                <Alert variant="warning" className="border-0 shadow-sm rounded-4 p-4 mb-4">
                    <div className="d-md-flex align-items-center justify-content-between gap-3">
                        <div className="d-flex align-items-center gap-3 mb-3 mb-md-0">
                            <div className="bg-white p-2 rounded-circle shadow-sm">
                                <LuCircleAlert className="text-warning" size={24} />
                            </div>
                            <div>
                                <h6 className="fw-bold mb-1">Payment is Pending</h6>
                                <p className="small mb-0 text-muted">
                                    The customer has not paid. Share the link below to collect payment.
                                </p>
                            </div>
                        </div>

                        {paymentLinkUrl ? (
                            <div className="d-flex gap-2 align-items-center flex-wrap">
                                <InputGroup size="sm" style={{ maxWidth: '230px' }}>
                                    <Form.Control
                                        readOnly
                                        value={paymentLinkUrl}
                                        className="bg-white border-end-0"
                                        style={{ fontSize: '11px' }}
                                    />
                                    <Button variant="outline-secondary" onClick={handleCopyLink} className="bg-white border-start-0">
                                        {copied ? <LuCheck className="text-success" /> : <LuCopy />}
                                    </Button>
                                </InputGroup>
                                <Button
                                    variant="warning"
                                    size="sm"
                                    className="rounded-pill px-3 fw-bold d-flex align-items-center gap-2 shadow-sm text-dark"
                                    onClick={() => window.open(paymentLinkUrl, '_blank')}
                                >
                                    <LuExternalLink size={16} /> Open Link
                                </Button>
                            </div>
                        ) : paymentLinkStatus === 'expired' ? (
                            <div className="d-flex align-items-center gap-3">
                                <span className="text-danger small fw-bold">Link Expired</span>
                                <DirectPaymentLink onSuccess={() => dispatch(fetchTransactionDetail(id))} />
                            </div>
                        ) : (
                            <div className="d-flex align-items-center gap-2 text-muted small">
                                <Spinner animation="border" size="sm" /> Retrieving link...
                            </div>
                        )}
                    </div>
                </Alert>
            )}

            {/* ── Main Content ─────────────────────────────────────────────────── */}
            <Row className="g-4">
                {/* Left: Details */}
                <Col lg={8}>
                    {/* Amount Header Card */}
                    <Card className="border-0 shadow-sm rounded-4 overflow-hidden mb-4">
                        <div className={`p-4 d-flex justify-content-between align-items-center ${colors.soft}`}>
                            <div>
                                <h3 className="fw-bold mb-1">
                                    {financials.totalAmount?.toFixed(2)} {financials.currency}
                                </h3>
                                <small className={`${colors.text} opacity-75 fw-semibold`}>
                                    ID: #{id.slice(-8).toUpperCase()}
                                </small>
                            </div>
                            <Badge
                                bg={colors.bg}
                                className={`rounded-pill px-4 py-2 ${status === 'pending' || status === 'voided' ? 'text-dark' : ''}`}
                                style={{ fontSize: '13px' }}
                            >
                                {status?.toUpperCase()}
                            </Badge>
                        </div>

                        <Card.Body className="p-4">
                            <Row className="g-4">
                                {/* Customer Info */}
                                <Col md={6}>
                                    <h6 className="fw-bold mb-3 d-flex align-items-center gap-2">
                                        <LuUser className="text-brand" size={16} /> Customer Info
                                    </h6>
                                    <div className="bg-light p-3 rounded-4">
                                        <p className="fw-bold mb-1">{customerId?.name || 'Unknown Customer'}</p>
                                        <p className="text-muted small mb-1">{customerId?.email || '—'}</p>
                                        <p className="text-muted small mb-0">{formatPhoneNumber(customerId?.phone) || '—'}</p>
                                        {customerId?.address && typeof customerId.address === 'object' && (
                                            <p className="text-muted small mb-0">
                                                {[
                                                    customerId.address.street,
                                                    customerId.address.city,
                                                    customerId.address.state,
                                                    customerId.address.zipCode,
                                                    customerId.address.country,
                                                ].filter(Boolean).join(', ')}
                                            </p>
                                        )}
                                    </div>
                                </Col>

                                {/* Reference */}
                                <Col md={6}>
                                    <h6 className="fw-bold mb-3 d-flex align-items-center gap-2">
                                        <LuBriefcase className="text-brand" size={16} />
                                        {invoiceId ? 'Invoice Reference' : 'Payment Reference'}
                                    </h6>
                                    <div className="bg-light p-3 rounded-4">
                                        <p className="fw-bold mb-1">
                                            {invoiceId
                                                ? `Invoice #${invoiceId.toString().slice(-6).toUpperCase()}`
                                                : (description || 'Direct Payment Link')}
                                        </p>
                                        {invoiceId && (
                                            <div className="d-flex align-items-center gap-2 text-muted small mt-1">
                                                <LuTag size={12} /> Linked to Invoice System
                                            </div>
                                        )}
                                        <div className="d-flex align-items-center gap-2 text-muted small mt-1">
                                            <LuReceipt size={12} /> {moment(createdAt).format('MMM DD, YYYY • hh:mm A')}
                                        </div>
                                    </div>
                                </Col>
                            </Row>

                            <hr className="my-4 opacity-25" />

                            {/* Payment Method */}
                            <h6 className="fw-bold mb-3 d-flex align-items-center gap-2">
                                <LuCreditCard className="text-brand" size={16} /> Payment Information
                            </h6>
                            <div className="d-flex align-items-center justify-content-between bg-light p-3 rounded-4">
                                <div className="d-flex align-items-center gap-3">
                                    <div className="bg-white p-2 rounded-3 shadow-sm">
                                        <LuCreditCard size={22} className="text-muted" />
                                    </div>
                                    <div>
                                        <p className="mb-0 fw-bold text-capitalize">
                                            {status === 'pending'
                                                ? 'Pending Stripe Checkout'
                                                : `${paymentMethod?.cardBrand || 'Card'} •••• ${paymentMethod?.last4 || '****'}`}
                                        </p>
                                        <small className="text-muted">Processed securely via Stripe</small>
                                    </div>
                                </div>
                                <div className="text-end">
                                    <small className="text-muted d-block fw-bold text-uppercase" style={{ fontSize: '10px' }}>
                                        {status === 'pending' ? 'Created' : 'Paid On'}
                                    </small>
                                    <small className="fw-bold">{moment(createdAt).format('DD MMM YYYY')}</small>
                                </div>
                            </div>
                        </Card.Body>
                    </Card>
                </Col>

                {/* Right: Financial Breakdown */}
                <Col lg={4}>
                    <Card className="border-0 shadow-sm rounded-4 p-4 sticky-top" style={{ top: '20px' }}>
                        <h6 className="fw-bold mb-4">Financial Breakdown</h6>

                        <div className="d-flex justify-content-between mb-3">
                            <span className="text-muted">Gross Amount</span>
                            <span className="fw-bold">
                                {financials.totalAmount?.toFixed(2)} {financials.currency}
                            </span>
                        </div>

                        {financials.platformFee > 0 && (
                            <div className="d-flex justify-content-between mb-3">
                                <span className="text-muted">Processing Fee</span>
                                <span className="text-danger">
                                    -{financials.platformFee?.toFixed(2)} {financials.currency}
                                </span>
                            </div>
                        )}

                        {financials.refundedAmount > 0 && (
                            <div className="d-flex justify-content-between mb-3">
                                <span className="text-muted">Refunded</span>
                                <span className="text-danger">
                                    -{financials.refundedAmount?.toFixed(2)} {financials.currency}
                                </span>
                            </div>
                        )}

                        <hr className="opacity-25" />

                        <div className="d-flex justify-content-between align-items-center">
                            <span className="fw-bold text-brand">Net Payout</span>
                            <h4 className="fw-bold text-brand mb-0">
                                {((financials.totalAmount || 0) - (financials.platformFee || 0) - (financials.refundedAmount || 0)).toFixed(2)} {financials.currency}
                            </h4>
                        </div>

                        {/* Quick Links */}
                        {status === 'pending' && paymentLinkUrl && (
                            <div className="mt-4 p-3 bg-warning-subtle rounded-3">
                                <small className="fw-bold d-block mb-2 text-warning-emphasis">⚡ Quick Actions</small>
                                <Button
                                    variant="warning"
                                    size="sm"
                                    className="w-100 rounded-pill fw-bold text-dark d-flex align-items-center justify-content-center gap-2"
                                    onClick={() => window.open(paymentLinkUrl, '_blank')}
                                >
                                    <LuExternalLink size={14} /> Open Payment Page
                                </Button>
                            </div>
                        )}

                        {status === 'captured' && (
                            <div className="mt-4 p-3 bg-soft-brand rounded-3">
                                <small className="fw-bold d-block mb-1 text-brand">✓ Payment Verified</small>
                                <small className="text-muted">This transaction was captured and settled successfully via Stripe.</small>
                            </div>
                        )}
                    </Card>
                </Col>
            </Row>

            {/* ── Refund Modal ──────────────────────────────────────────────── */}
            <Modal show={showRefundModal} onHide={() => setShowRefundModal(false)} centered>
                <Modal.Header closeButton className="border-0 pb-0">
                    <Modal.Title className="fw-bold">Process Refund</Modal.Title>
                </Modal.Header>
                <Modal.Body className="pt-3 px-4">
                    <Form.Group className="mb-4">
                        <Form.Label className="small fw-bold text-muted">Refund Type</Form.Label>
                        <div className="d-flex gap-4">
                            <Form.Check
                                type="radio" label="Full Refund" name="refundType" id="full"
                                checked={refundType === 'full'}
                                onChange={() => setRefundType('full')}
                            />
                            <Form.Check
                                type="radio" label="Partial Refund" name="refundType" id="partial"
                                checked={refundType === 'partial'}
                                onChange={() => setRefundType('partial')}
                            />
                        </div>
                    </Form.Group>

                    {refundType === 'partial' && (
                        <Form.Group className="mb-3">
                            <Form.Label className="small fw-bold text-muted">
                                Amount ({financials.currency}) — Max: {financials.totalAmount?.toFixed(2)}
                            </Form.Label>
                            <Form.Control
                                type="number"
                                placeholder="0.00"
                                className="rounded-3 shadow-none"
                                value={refundAmount}
                                onChange={(e) => setRefundAmount(e.target.value)}
                                max={financials.totalAmount}
                            />
                        </Form.Group>
                    )}

                    <Form.Group className="mb-3">
                        <Form.Label className="small fw-bold text-muted">Reason for Refund</Form.Label>
                        <Form.Control
                            as="textarea" rows={3}
                            placeholder="e.g. Customer cancellation, duplicate charge..."
                            className="rounded-3 shadow-none"
                            value={refundReason}
                            onChange={(e) => setRefundReason(e.target.value)}
                        />
                    </Form.Group>
                </Modal.Body>
                <Modal.Footer className="border-0 pt-0 px-4 pb-4">
                    <Button variant="light" className="rounded-pill px-4" onClick={() => setShowRefundModal(false)}>
                        Cancel
                    </Button>
                    <Button
                        variant="danger"
                        className="rounded-pill px-4 fw-bold"
                        onClick={handleRefund}
                        disabled={isProcessing}
                    >
                        {isProcessing ? <Spinner size="sm" /> : 'Confirm Refund'}
                    </Button>
                </Modal.Footer>
            </Modal>

            <style>{`
                @media print {
                    .btn, nav, .sidebar-container { display: none !important; }
                }
            `}</style>
        </Container>
    );
};

export default TransactionDetail;
